/**
 * Locale-aware formatting built on Intl. Pure module (no React) so the
 * formatters are usable from tests and from non-component code that receives a
 * locale. `Intl.*Format` construction is expensive and the Home page re-renders
 * on a 10s poll, so instances are cached per (locale, options).
 */

import type { Locale } from './locale'

const dateTimeCache = new Map<string, Intl.DateTimeFormat>()
const numberCache = new Map<string, Intl.NumberFormat>()

function dateTimeFormatter(locale: Locale, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${locale}|${JSON.stringify(options)}`
  let formatter = dateTimeCache.get(key)
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, options)
    dateTimeCache.set(key, formatter)
  }
  return formatter
}

function numberFormatter(locale: Locale, options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = `${locale}|${JSON.stringify(options)}`
  let formatter = numberCache.get(key)
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, options)
    numberCache.set(key, formatter)
  }
  return formatter
}

function toDate(value: Date | string | number): Date {
  return value instanceof Date ? value : new Date(value)
}

/** e.g. en: "Jul 20, 2026" · pt-BR: "20 de jul. de 2026" */
export function formatDate(locale: Locale, value: Date | string | number): string {
  return dateTimeFormatter(locale, { year: 'numeric', month: 'short', day: 'numeric' }).format(toDate(value))
}

/** e.g. en: "Jul 20, 14:03" · pt-BR: "20 de jul., 14:03" */
export function formatDateTime(locale: Locale, value: Date | string | number): string {
  return dateTimeFormatter(locale, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }).format(
    toDate(value)
  )
}

/** e.g. en: "2:03 PM" / "14:03" per locale conventions. */
export function formatTime(locale: Locale, value: Date | string | number): string {
  return dateTimeFormatter(locale, { hour: '2-digit', minute: '2-digit' }).format(toDate(value))
}

export function formatNumber(locale: Locale, value: number, options?: Intl.NumberFormatOptions): string {
  return numberFormatter(locale, options ?? {}).format(value)
}

const relativeCache = new Map<string, Intl.RelativeTimeFormat>()

function relativeFormatter(locale: Locale): Intl.RelativeTimeFormat {
  let formatter = relativeCache.get(locale)
  if (!formatter) {
    formatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'narrow' })
    relativeCache.set(locale, formatter)
  }
  return formatter
}

/**
 * Relative "ago" rendering via Intl.RelativeTimeFormat (locale-driven, no
 * catalog prose): en "5 min. ago" · pt-BR "há 5 min.". Replaces the package's
 * English-only `fmt.ago`.
 */
export function formatAgo(locale: Locale, value: Date | string | number, nowMs: number = Date.now()): string {
  const then = toDate(value).getTime()
  if (Number.isNaN(then)) return '—'
  const diff = Math.max(0, nowMs - then)
  const rtf = relativeFormatter(locale)
  if (diff < 60_000) return rtf.format(0, 'minute')
  if (diff < 3_600_000) return rtf.format(-Math.floor(diff / 60_000), 'minute')
  if (diff < 86_400_000) return rtf.format(-Math.floor(diff / 3_600_000), 'hour')
  return rtf.format(-Math.floor(diff / 86_400_000), 'day')
}

/** Compact duration (locale decimal separator): 850ms · 1.2s / 1,2s · 3.5m / 3,5m. */
export function formatDuration(locale: Locale, ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return '—'
  if (ms < 1000) return `${Math.round(ms)}ms`
  if (ms < 60_000) return `${formatNumber(locale, ms / 1000, { maximumFractionDigits: 1 })}s`
  if (ms < 3_600_000) return `${formatNumber(locale, ms / 60_000, { maximumFractionDigits: 1 })}m`
  return `${formatNumber(locale, ms / 60_000, { maximumFractionDigits: 0 })}m`
}

/**
 * Size in decimal (SI) units with the locale's separator: 1.2 MB / 1,2 MB, where 1 MB = 1,000,000 bytes — as file
 * managers and the stated upload limits count. (It divided by 1024 and printed "MB": a 10 MiB cap read "10 MB" while
 * a 10.4 MB file was refused under it.)
 */
export function formatBytes(locale: Locale, bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—'
  if (bytes < 1000) return `${formatNumber(locale, bytes, { maximumFractionDigits: 0 })} B`
  const units = ['KB', 'MB', 'GB'] as const
  let value = bytes
  let unit: string = 'B'
  // The unit is chosen on the ROUNDED value: 999,950 bytes is "1 MB", never "1,000 KB".
  for (const next of units) {
    if (unit !== 'B' && Math.round(value * 10) / 10 < 1000) break
    value /= 1000
    unit = next
  }
  return `${formatNumber(locale, value, { maximumFractionDigits: 1 })} ${unit}`
}
